import React, { useEffect, useState } from 'react';
import { IUser } from '../../models';
import {
  MonthView,
  Scheduler,
  SchedulerItem,
  SchedulerSlot,
  SchedulerSlotProps,
  WeekView,
  WorkWeekView,
} from '@progress/kendo-react-scheduler';

import './orderCalendar.scss';
import { pictureStorageUrl } from '../../appConfig';
import { Spinner2 } from '../shared/spinner2';
import { IOrderUpdateDialogState } from '../orderOverview/OrderOverview';
import {
  filterByDropdowns,
  getCalendarFromdate,
  getCalendarTodate,
  getDefaultCalendarView,
} from './filters';
import { locale } from '../../common/localization/localizationService';
import { IFraction } from '../../models/Fraction.model';
import moment from 'moment';
import NewOrderDialog from '../dashboard/newOrderModal';
import { ISideFilter } from '../../models/SideFilterObject.model';
export interface IOrderCalendarProps {
  authentication: IUser;
  orderActions: any;
  orderOverview: Array<any>;
  calendarMonth: Date;
  isLoading: boolean;
  isFilterVisible: boolean;
  orderOverviewFilter: ISideFilter;
  calendarView: string;
  allFractions: Array<IFraction>;
}

export default function OrderCalendar(props: IOrderCalendarProps): JSX.Element {
  let pictureCounter = 1;

  const editDialog: IOrderUpdateDialogState = {
    show: false,
    order: null,
  };

  const [orders, setOrders] = useState<Array<any>>([]);

  const [updateDialog, setUpdateDialog] =
    useState<IOrderUpdateDialogState>(editDialog);

  const adjustEventHeights = () => {
    const schedulerCells = document.querySelectorAll('.k-slot-cell');
    const events = document.querySelectorAll('.k-event');
    if (schedulerCells.length > 0 && events.length > 0) {
      schedulerCells.forEach((cell, index) => {
        const cellHeight = cell.clientHeight - 34;
        if (events[index]) {
          (events[index] as HTMLElement).style.maxHeight = `${cellHeight}px`;
        }
      });
    }
  };

  useEffect(() => {
    setDefaultCalendarView();
    adjustEventHeights();

    window.addEventListener('resize', adjustEventHeights);

    return () => {
      window.removeEventListener('resize', adjustEventHeights);
    };
  }, []);

  useEffect(() => {
    const filteredOrders = filterByDropdowns(
      props.orderOverview,
      props.orderOverviewFilter,
    );

    const groupedByDate = groupingOrdersByDate(filteredOrders);

    const result = Object.values(groupedByDate);

    setOrders(formatOrdersToCalendarItems(result));
    setTimeout(() => {
      adjustEventHeights();
    }, 250);
  }, [
    props.orderOverview,
    props.orderOverviewFilter.contractorIds,
    props.orderOverviewFilter.fractionIds,
    props.orderOverviewFilter.statusIds,
    props.orderOverviewFilter.routeIds,
  ]);

  useEffect(() => {
    setTimeout(() => {
      adjustEventHeights();
    }, 50);
  }, [props.isFilterVisible]);

  const handleDateChange = (event: any) => {
    const date = event.value;
    props.orderActions.setCalendarMonth(date);
    const filterObject = props.orderOverviewFilter;
    filterObject.fromDate = getCalendarFromdate(date, props.calendarView);
    filterObject.toDate = getCalendarTodate(date, props.calendarView);
    props.orderActions.setOrderCalendarFilter(filterObject);
    filterOrders();
  };

  const formatOrdersToCalendarItems = (orders: any): any => {
    return orders.map((dataItem: any) => ({
      id: dataItem.date,
      start: new Date(dataItem.date),
      end: new Date(new Date(dataItem.date).setHours(10)),
      isAllDay: true,
      title: '',
      description: '',
      personId: props.authentication.customerId,
      orders: dataItem.orders,
      roomId: dataItem.date,
      ownerID: props.authentication.customerId,
    }));
  };

  const groupingOrdersByDate = (orders: Array<any>) => {
    const groupedByDate = orders.reduce(
      (acc, order) => {
        const startDate = new Date(order.date);
        const expiresDate = new Date(order.expires);

        const currentDate = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
        ); // Ensures no timezone shift

        while (currentDate <= expiresDate) {
          const dateKey = moment(
            new Date(currentDate),
            'YYYY-MM-DD',
            true,
          ).toString();

          if (!acc[dateKey]) {
            acc[dateKey] = {
              date: dateKey,
              orders: [],
            };
          }

          acc[dateKey].orders.push({ ...order });

          currentDate.setDate(currentDate.getDate() + 1);
        }

        return acc;
      },
      {} as Record<string, { date: string; orders: Array<any> }>,
    );

    return groupedByDate;
  };

  const handleViewChange = (event: any) => {
    props.orderActions.setCalenderView(event.value);
    const filterObject = props.orderOverviewFilter;
    filterObject.fromDate = getCalendarFromdate(
      props.calendarMonth,
      event.value,
    );
    filterObject.toDate = getCalendarTodate(props.calendarMonth, event.value);
    props.orderActions.setOrderCalendarFilter(filterObject);
    filterOrders();
  };

  const editOrder = (order: any) => {
    order.date = new Date(order.date);
    order.ordinaryDate = new Date(order.ordinaryDate);
    order.expires = new Date(order.expires);
    props.orderActions.getAllFractions();
    const dialogProps = {
      order: order,
      show: true,
    };
    setUpdateDialog(dialogProps);
    setTimeout(() => {
      adjustEventHeights();
    }, 250);
  };

  const hideUpdateOrderDialog = (rerender = false) => {
    const dialogProps = {
      order: {},
      show: false,
    };
    setUpdateDialog(dialogProps);
    if (rerender) {
      const formattedFromDate = props.orderOverviewFilter.fromDate
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '');
      const formattedToDate = props.orderOverviewFilter.toDate
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '');
      props.orderActions.getOverView(formattedFromDate, formattedToDate, true);
    }
    setTimeout(() => {
      adjustEventHeights();
    }, 250);
  };

  const filterOrders = () => {
    if (
      props.orderOverviewFilter.fromDate &&
      props.orderOverviewFilter.toDate
    ) {
      const formattedFromDate = props.orderOverviewFilter.fromDate
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '');
      const formattedToDate = props.orderOverviewFilter.toDate
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '');
      props.orderActions.getOverView(
        formattedFromDate,
        formattedToDate,
        true,
        false,
      );
    }
  };

  const setDefaultCalendarView = () => {
    const defaultView = getDefaultCalendarView();
    props.orderActions.setCalenderView(defaultView);

    const filterObject = props.orderOverviewFilter;
    filterObject.fromDate = getCalendarFromdate(new Date(), defaultView);
    filterObject.toDate = getCalendarTodate(new Date(), defaultView);
    props.orderActions.setOrderCalendarFilter(filterObject);
    filterOrders();
  };

  const CustomOrderCalendarItem = (props: any) => {
    return (
      <>
        <SchedulerItem {...props}>
          <div className="order-calendar-item">
            {props.dataItem.orders &&
              props.dataItem.orders.map((order: any) => (
                <div
                  key={`${order.date}-${pictureCounter++}`}
                  className="calendar-order"
                  onClick={() => editOrder(order)}
                >
                  <div>{order.routeData.name}</div>
                  <div className="fraction-icons">
                    {order.fractions &&
                      order.fractions
                        .filter(
                          (frac: any, index: any, self: any) =>
                            index ===
                            self.findIndex((t: any) => t.name === frac.name),
                        ) // This ensures each name appears only once
                        .map((frac: any) => (
                          <img
                            className="fraction-picture"
                            title={frac.name}
                            src={`${pictureStorageUrl}${frac.url}`}
                            key={`frac-pic-${pictureCounter++}`}
                          />
                        ))}
                  </div>
                </div>
              ))}
          </div>
        </SchedulerItem>
      </>
    );
  };

  return (
    <>
      <div
        className={`order-calendar-view ${
          props.isFilterVisible ? 'filter-open' : ''
        }`}
      >
        <div className="calendar-content">
          <Spinner2 show={props.isLoading} />
          <Scheduler
            data={orders}
            defaultDate={props.calendarMonth}
            className="order-calendar"
            item={CustomOrderCalendarItem}
            onDateChange={handleDateChange}
            onViewChange={handleViewChange}
            view={props.calendarView}
          >
            <WeekView
              title={locale.calendar.week}
              slot={(props: SchedulerSlotProps) => <SchedulerSlot {...props} />}
            />
            <WorkWeekView
              title={locale.calendar.workweek}
              slot={(props: SchedulerSlotProps) => <SchedulerSlot {...props} />}
            />
            <MonthView
              title={locale.calendar.month}
              selectedDateFormat="{0:M}"
              selectedShortDateFormat="{0:Y}"
              slot={(props: SchedulerSlotProps) => <SchedulerSlot {...props} />}
            />
          </Scheduler>
        </div>
        {updateDialog.show && (
          <NewOrderDialog
            show={updateDialog.show}
            onHide={hideUpdateOrderDialog}
            orderToUpdate={updateDialog.order}
          />
        )}
      </div>
    </>
  );
}
